import '@/assets/styles/components/meet-toast.styles.scss';
import { Button } from '@/components';
import { useMeeting } from '@/context/meeting-context';
import { CALL_STATE_TYPES } from '@/constants/socket.ts';
import { useAccurateCountdown } from '@/utility/use-util.tsx';
import { useEffect } from 'react';
import clsx from 'clsx';
import { useDevicePermissions } from '@/context/device-permission-context.tsx';

type MeetToastHeaderProps = {
	message: string;
};

type UserInformationProps = {
	callState: string;
	socketData: {
		handleMatch?: {
			gradeName?: string;
			studentName?: string;
			studentSurname?: string;
		};
	};
};

type ActionButtonProps = {
	isAcceptedCall: boolean;
	callState: string;
	socketData: any;
	onApprove: () => void;
	onStartCall: () => void;
};

const MeetToastHeader = ({ message }: MeetToastHeaderProps) => (
	<div className="meet-toast-header">
		<div className="meet-toast-header-tag">
			<span>{message}</span>
		</div>
	</div>
);

const UserInformation = ({ callState, socketData }: UserInformationProps) => {
	const subTitle = callState === CALL_STATE_TYPES.FOUND && socketData?.handleMatch?.gradeName && `${socketData?.handleMatch?.gradeName}`;

	const getFullName = () => {
		const fullName = socketData?.handleMatch?.studentName + socketData?.handleMatch?.studentSurname;

		if (fullName?.length > 15) {
			return socketData?.handleMatch?.studentName + ' ' + socketData?.handleMatch?.studentSurname?.substring(0, 1) + '.';
		}

		return socketData?.handleMatch?.studentName + ' ' + socketData?.handleMatch?.studentSurname;
	};

	return (
		<div className="meet-toast-content-user-information">
			<span className="meet-toast-content-user-information-user-label">{callState === CALL_STATE_TYPES.SEARCHING ? 'Öğrenci Aranıyor' : getFullName()}</span>
			<span className="meet-toast-content-user-information-user-state">{subTitle || ''}</span>
		</div>
	);
};

const ActionButton = ({ isAcceptedCall, callState, onApprove, onStartCall, socketData }: ActionButtonProps) => {
	const meetingPreparationTimer = useAccurateCountdown();
	const { controlPermission } = useDevicePermissions();

	const { matchApproved } = socketData;

	useEffect(() => {
		if (matchApproved?.meetingPreparationCompletionDate) {
			meetingPreparationTimer?.reset(matchApproved?.meetingPreparationCompletionDate);
		}
	}, [matchApproved?.meetingPreparationCompletionDate]);

	return (
		<div className="meet-toast-content-user-information-button">
			{callState === CALL_STATE_TYPES.FOUND && !isAcceptedCall ? (
				<Button
					size="medium"
					variant="success"
					onClick={() => controlPermission(onApprove)}>
					<span>Kabul Et</span>
				</Button>
			) : (
				<Button
					size="medium"
					variant="success"
					onClick={() => controlPermission(onStartCall)}>
					{meetingPreparationTimer.isCompleted ? (
						<span>Bağlan</span>
					) : (
						<>
							<b>{meetingPreparationTimer.formattedTime}</b>
							<span style={{ fontWeight: 200 }}> sn</span>
						</>
					)}
				</Button>
			)}
		</div>
	);
};

export default function MeetToast({ isVisible }: { isVisible: boolean }) {
	const { callState, onApprove, socketData, onStartCall, isAcceptedCall, hasActiveMeeting, onConnectPastMeeting } = useMeeting();

	const getHeaderMessage = () => {
		let message = '';
		if (callState === CALL_STATE_TYPES.FOUND) {
			message = 'Öğrenci Bulundu';
		}

		if (callState === CALL_STATE_TYPES.FOUND && isAcceptedCall) {
			message = 'Bekleyen Öğrenci';
		}

		return message;
	};

	const handleStartCall = () => {
		if (hasActiveMeeting) {
			onConnectPastMeeting();
			return;
		}

		onStartCall();
	};

	useEffect(() => {
		if (isVisible) {
			document.body.classList.add('toast-visible');
		} else {
			document.body.classList.remove('toast-visible');
		}
		return () => {
			document.body.classList.remove('toast-visible');
		};
	}, [isVisible]);

	return (
		<div className={clsx('meet-toast-container', { 'toast-active': isVisible })}>
			<div className={clsx('meet-toast', isVisible ? 'visible' : 'hidden')}>
				{getHeaderMessage() && <MeetToastHeader message={getHeaderMessage()} />}
				<div className="meet-toast-content">
					<div className="meet-toast-content-avatar">
						{socketData?.handleMatch?.studentAvatarUrl ? (
							<img
								src={socketData?.handleMatch?.studentAvatarUrl}
								alt={socketData?.handleMatch?.studentName}
							/>
						) : (
							<i className="icons-user-circle" />
						)}
					</div>
					<UserInformation
						callState={callState}
						socketData={socketData}
					/>
					{callState === CALL_STATE_TYPES.FOUND && (
						<ActionButton
							callState={callState}
							socketData={socketData}
							isAcceptedCall={isAcceptedCall}
							onApprove={onApprove}
							onStartCall={handleStartCall}
						/>
					)}
				</div>
			</div>
		</div>
	);
}
